/**
 * Stuck Bins List Component
 *
 * Displays bins held longer than the stuck threshold.
 * Queries prod_bins directly (not prod_issues) since stuck bin detection
 * is computed at query time based on assigned_at vs threshold hours.
 */
import { useTranslation } from 'react-i18next';
import { Archive, Clock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { skipToken } from '@tanstack/react-query';
import { formatOrderNumber } from '@threadheads/shared';

interface StuckBinsListProps {
  locationId: string;
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

export function StuckBinsList({ locationId }: StuckBinsListProps) {
  const { t } = useTranslation('production');
  const { data: bins, isLoading } = trpc.bins.getLocationBins.useQuery(
    locationId ? { locationId } : skipToken,
    {
      staleTime: 30_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    }
  );

  const stuckBins = bins?.filter(b => b.isStuck) ?? [];

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (stuckBins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Archive className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <p className="mt-4 text-lg font-medium">{t('issues.stuckBins.noStuck')}</p>
        <p className="text-sm text-muted-foreground">
          {t('issues.stuckBins.willAppearHere')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t('issues.stuckBins.countBeyondThreshold', { count: stuckBins.length })}
      </p>
      {stuckBins
        .sort((a, b) => (b.timeInBinMs ?? 0) - (a.timeInBinMs ?? 0))
        .map((bin) => (
        <Card key={bin.id} className="border-l-4 border-l-amber-500">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <span className="text-lg font-bold">{bin.label}</span>
                {bin.orderName && (
                  <span className="text-sm text-muted-foreground">{formatOrderNumber(bin.orderName)}</span>
                )}
              </div>
              <Badge variant="outline" className="gap-1">
                <Clock className="h-3 w-3" />
                {bin.timeInBinMs ? formatDuration(bin.timeInBinMs) : t('issues.stuckBins.unknown')}
              </Badge>
              <Badge variant="secondary">
                {t('issues.stuckBins.caughtCount', { caught: bin.itemsCaught, total: bin.totalItems })}
              </Badge>
              {bin.hasRedoPending && (
                <Badge variant="destructive">{t('issues.stuckBins.redoPending')}</Badge>
              )}
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/production/bins/manage">
                <ExternalLink className="h-4 w-4 mr-1" />
                {t('issues.stuckBins.manage')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
